/**
 * Order-abgeleitete Aggregate als REINE Funktionen — eine Quelle für Server (server-core
 * Read-Model) und PWA (lokale Projektion aus dem Event-Stream im Mobilfunk-Betrieb). Kein I/O.
 */

import type { EventEnvelope } from "./events.js";
import type { Order } from "./model/order.js";
import { stationAtOf } from "./model/order.js";
import { applyOrderEvent } from "./projection.js";
import { compareHlc } from "./hlc.js";

/** Zeitfenster für die Wartezeit-Schätzung: nur Freigaben der letzten 30 min zählen. */
export const WAIT_STATS_WINDOW_MS = 30 * 60_000;

/**
 * Aus einer Event-Folge alle Orders projizieren. Gruppiert nach orderId und wendet je Order ihre
 * Events **HLC-sortiert** an (robust gegen unsortierten Pull), Config-Snapshots werden ignoriert.
 */
export function projectOrders(events: EventEnvelope[]): Order[] {
  const byId = new Map<string, EventEnvelope[]>();
  for (const e of events) {
    if (e.body.kind === "config.snapshot") continue;
    const arr = byId.get(e.body.orderId) ?? [];
    arr.push(e);
    byId.set(e.body.orderId, arr);
  }
  const out: Order[] = [];
  for (const evs of byId.values()) {
    evs.sort((a, b) => compareHlc(a.hlc, b.hlc));
    let order: Order | null = null;
    for (const e of evs) order = applyOrderEvent(order, e);
    if (order) out.push(order);
  }
  return out;
}

/**
 * Verkaufte Menge je Artikel (menuItemId → Summe bestellter qty). Zählt nicht-stornierte
 * Positionen nicht-stornierter Bestellungen ("verkauft = bei Bestellung"). Grundlage Bestand.
 */
export function soldCounts(orders: Order[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const o of orders) {
    if (o.status === "void") continue;
    for (const l of o.lines) {
      if (l.status === "voided") continue;
      out[l.menuItemId] = (out[l.menuItemId] ?? 0) + l.qty;
    }
  }
  return out;
}

/**
 * Wartezeit je Artikel (Freigabe minus Ankunft an der Station: Senden bzw. Zahlung), gemittelt über die letzten ≤10 Freigaben im
 * Fenster. Nur aktuelle Freigaben zählen (alte Werte sind für die jetzige Lage hinfällig).
 */
export function waitStats(
  orders: Order[],
  nowMs: number,
  windowMs = WAIT_STATS_WINDOW_MS,
): Record<string, { avgMs: number; count: number; name: string }> {
  const cutoff = nowMs - windowMs;
  const perItem = new Map<string, { atMs: number; wait: number; name: string }[]>();
  for (const o of orders) {
    for (const s of o.served ?? []) {
      if (s.atMs < cutoff) continue; // alte Freigaben verwerfen
      const line = o.lines.find((l) => l.id === s.lineId);
      if (!line) continue;
      // Wartezeit ab Ankunft an der Station: Sendezeit (lines.sent) bzw. Zahlungszeit.
      const since = stationAtOf(o, s.lineId);
      if (since == null) continue;
      const wait = s.atMs - since;
      if (wait < 0) continue;
      const arr = perItem.get(line.menuItemId) ?? [];
      arr.push({ atMs: s.atMs, wait, name: line.name });
      perItem.set(line.menuItemId, arr);
    }
  }
  const out: Record<string, { avgMs: number; count: number; name: string }> = {};
  for (const [id, arr] of perItem) {
    const recent = arr.sort((a, b) => b.atMs - a.atMs).slice(0, 10);
    out[id] = {
      avgMs: Math.round(recent.reduce((s, x) => s + x.wait, 0) / recent.length),
      count: recent.length,
      name: recent[0]!.name,
    };
  }
  return out;
}
